var __spreadArray = this && this.__spreadArray || function (to, from, pack) {
  if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
    if (ar || !(i in from)) {
      if (!ar) ar = Array.prototype.slice.call(from, 0, i);
      ar[i] = from[i];
    }
  }
  return to.concat(ar || Array.prototype.slice.call(from));
};

import { display } from '../../tools/display';
import { toStackTraceString } from '../../tools/error';
import { assign, combine, jsonStringify, performDraw, includes, startsWith } from '../../tools/utils';
import { INTAKE_SITE_STAGING, INTAKE_SITE_US1_FED } from '../configuration';
import { computeStackTrace } from '../tracekit';
import { Observable } from '../../tools/observable';
import { timeStampNow } from '../../tools/timeUtils';
import { ConsoleApiName } from '../console/consoleObservable';
var ALLOWED_FRAME_URLS = ['https://www.datadoghq-browser-agent.com', 'https://www.datad0g-browser-agent.com', 'http://localhost', '<anonymous>'];
var TELEMETRY_EXCLUDED_SITES = [INTAKE_SITE_US1_FED];
var telemetryConfiguration = {
  maxEventsPerPage: 0,
  sentEventCount: 0,
  telemetryEnabled: false
};
var onRawTelemetryEventCollected;
export function startTelemetry(configuration) {
  var contextProvider;
  var observable = new Observable();
  telemetryConfiguration.telemetryEnabled = performDraw(configuration.telemetrySampleRate);

  onRawTelemetryEventCollected = function onRawTelemetryEventCollected(event) {
    if (!includes(TELEMETRY_EXCLUDED_SITES, configuration.site) && telemetryConfiguration.telemetryEnabled) {
      observable.notify(toTelemetryEvent(event));
    }
  };

  assign(telemetryConfiguration, {
    maxEventsPerPage: configuration.maxTelemetryEventsPerPage,
    sentEventCount: 0
  });

  function toTelemetryEvent(event) {
    return combine({
      type: 'telemetry',
      date: timeStampNow(),
      service: 'browser-sdk',
      version: "4.11.1",
      source: 'browser',
      _dd: {
        format_version: 2
      },
      telemetry: event // https://github.com/microsoft/TypeScript/issues/48457

    }, contextProvider !== undefined ? contextProvider() : {});
  }

  return {
    setContextProvider: function setContextProvider(provider) {
      contextProvider = provider;
    },
    observable: observable
  };
}
export function startFakeTelemetry() {
  var events = [];
  assign(telemetryConfiguration, {
    maxEventsPerPage: Infinity,
    sentEventCount: 0
  });

  onRawTelemetryEventCollected = function onRawTelemetryEventCollected(event) {
    events.push(event);
  };

  return events;
}
export function resetTelemetry() {
  onRawTelemetryEventCollected = undefined;
  telemetryConfiguration.debugMode = undefined;
}
/**
 * Avoid mixing telemetry events from different data centers
 * but keep replicating staging events for reliability
 */

export function isTelemetryReplicationAllowed(configuration) {
  return configuration.site === INTAKE_SITE_STAGING;
}
export function monitored(_, __, descriptor) {
  var originalMethod = descriptor.value;

  descriptor.value = function () {
    var args = [];

    for (var _i = 0; _i < arguments.length; _i++) {
      args[_i] = arguments[_i];
    }

    var decorated = onRawTelemetryEventCollected ? monitor(originalMethod) : originalMethod;
    return decorated.apply(this, args);
  };
}
export function monitor(fn) {
  return function () {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return callMonitored(fn, this, arguments);
  }; // consider output type has input type
}
export function callMonitored(fn, context, args) {
  try {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return fn.apply(context, args);
  } catch (e) {
    displayIfDebugEnabled(ConsoleApiName.error, e);

    try {
      addTelemetryError(e);
    } catch (e) {
      displayIfDebugEnabled(ConsoleApiName.error, e);
    }
  }
}
export function addTelemetryDebug(message, context) {
  displayIfDebugEnabled(ConsoleApiName.debug, message, context);
  addTelemetry(assign({
    message: message,
    status: "debug"
    /* debug */

  }, context));
}
export function addTelemetryError(e) {
  addTelemetry(assign({
    status: "error"
    /* error */

  }, formatError(e)));
}

function addTelemetry(event) {
  if (onRawTelemetryEventCollected && telemetryConfiguration.sentEventCount < telemetryConfiguration.maxEventsPerPage) {
    telemetryConfiguration.sentEventCount += 1;
    onRawTelemetryEventCollected(event);
  }
}

function formatError(e) {
  if (e instanceof Error) {
    var stackTrace = computeStackTrace(e);
    return {
      error: {
        kind: stackTrace.name,
        stack: toStackTraceString(scrubCustomerFrames(stackTrace))
      },
      message: stackTrace.message
    };
  }

  return {
    error: {
      stack: 'Not an instance of error'
    },
    message: "Uncaught ".concat(jsonStringify(e))
  };
}

export function scrubCustomerFrames(stackTrace) {
  stackTrace.stack = stackTrace.stack.filter(function (frame) {
    return !frame.url || ALLOWED_FRAME_URLS.some(function (allowedFrameUrl) {
      return startsWith(frame.url, allowedFrameUrl);
    });
  });
  return stackTrace;
}
export function setDebugMode(debugMode) {
  telemetryConfiguration.debugMode = debugMode;
}

function displayIfDebugEnabled(api) {
  var args = [];

  for (var _i = 1; _i < arguments.length; _i++) {
    args[_i - 1] = arguments[_i];
  }

  if (telemetryConfiguration.debugMode) {
    display.apply(void 0, __spreadArray([api, '[TELEMETRY]'], args, false));
  }
}